"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useRenderLabels = void 0;
const tslib_1 = require("tslib");
const catalog_core_1 = require("@tableau/catalog-core");
const catalog_messages_1 = require("@tableau/catalog-messages");
const catalog_queries_1 = require("@tableau/catalog-queries");
const widgets_1 = require("@tableau/widgets");
const react_query_1 = require("@tanstack/react-query");
const react_1 = tslib_1.__importStar(require("react"));
const useLockInLoadedValue_1 = require("../../hooks/useLockInLoadedValue");
const useTelemetrySender_1 = require("../../hooks/useTelemetrySender");
const TelemetryContext_1 = require("../../telemetry/TelemetryContext");
const LabelButtons_1 = require("../LabelButtons");
const Labels_1 = require("../restApi/Labels");
const LabelTypes_1 = require("../restApi/LabelTypes");
const LabelCategories_1 = require("../shared/LabelCategories");
const Styles_1 = require("../Styles");
const getUsers_1 = require("./getUsers");
const useColumnLineageLabelsLazyQuery_1 = require("./lazyQueries/useColumnLineageLabelsLazyQuery");
const useDatabaseTableLineageLabelsLazyQuery_1 = require("./lazyQueries/useDatabaseTableLineageLabelsLazyQuery");
const useFieldLineageLabelsLazyQuery_1 = require("./lazyQueries/useFieldLineageLabelsLazyQuery");
const useFlowColumnFieldLineageLabelsLazyQuery_1 = require("./lazyQueries/useFlowColumnFieldLineageLabelsLazyQuery");
const useFlowFieldLineageLabelsLazyQuery_1 = require("./lazyQueries/useFlowFieldLineageLabelsLazyQuery");
const useFlowLineageLabelsLazyQuery_1 = require("./lazyQueries/useFlowLineageLabelsLazyQuery");
const useGeneralLineageLabelsLazyQuery_1 = require("./lazyQueries/useGeneralLineageLabelsLazyQuery");
const usePublishedDatasourceLineageLabelsLazyQuery_1 = require("./lazyQueries/usePublishedDatasourceLineageLabelsLazyQuery");
const useVirtualConnectionLineageLabelsLazyQuery_1 = require("./lazyQueries/useVirtualConnectionLineageLabelsLazyQuery");
const useVirtualConnectionTableLineageLabelsLazyQuery_1 = require("./lazyQueries/useVirtualConnectionTableLineageLabelsLazyQuery");
const useWorkbookLineageLabelsLazyQuery_1 = require("./lazyQueries/useWorkbookLineageLabelsLazyQuery");
const StickersFlexBox_1 = require("./StickersFlexBox");
const defaultChipDisplayCount = 8;
function getRestLabels({ makeJsonRestApiRequest, items }) {
    return tslib_1.__awaiter(this, void 0, void 0, function* () {
        if (!items.contentType || !(0, LabelTypes_1.isLabelContentType)(items.contentType) || !items.contentLuid) {
            return [];
        }
        const result = yield (0, Labels_1.getLabelsSimple)({
            contentItems: [{
                    contentId: items.contentLuid,
                    contentType: items.contentType,
                    contentName: items.contentName,
                }],
            makeJsonRestApiRequest,
            transform: (t) => t,
        });
        // const labels = result.get({ contentId: items.contentLuid, contentType: items.contentType.toLowerCase() })
        return result !== null && result !== void 0 ? result : [];
    });
}
function useLabelsGraph(item, controls, includeUpstream, includeParented, contextOverrides) {
    var _a;
    const column = (0, useColumnLineageLabelsLazyQuery_1.useColumnLineageLabelsLazyQuery)({
        item,
        controls,
        includeUpstream,
        includeParented,
        contextOverrides,
        document: catalog_queries_1.ColumnLineageLabelsDocument,
    });
    const database = (0, useGeneralLineageLabelsLazyQuery_1.useGeneralLineageLabelsLazyQuery)({
        item,
        controls,
        includeUpstream,
        includeParented,
        contextOverrides,
        document: catalog_queries_1.DatabaseLineageLabelsDocument,
    });
    const databaseTable = (0, useDatabaseTableLineageLabelsLazyQuery_1.useDatabaseTableLineageLabelsLazyQueryInheritedLabels)({
        item,
        controls,
        includeUpstream,
        includeParented,
        contextOverrides,
        document: catalog_queries_1.DatabaseTableLineageLabelsDocument,
    });
    const virtualConnectionTable = (0, useVirtualConnectionTableLineageLabelsLazyQuery_1.useVirtualConnectionTableLineageLabelsLazyQuery)({
        item,
        controls,
        includeUpstream,
        includeParented,
        contextOverrides,
        document: catalog_queries_1.VirtualConnectionTablesLineageLabelsDocument,
    });
    const flow = (0, useFlowLineageLabelsLazyQuery_1.useFlowLineageLabelsLazyQuery)({
        item,
        controls,
        includeUpstream,
        includeParented,
        contextOverrides,
        document: catalog_queries_1.FlowLineageLabelsDocument,
    });
    const publishedDatasource = (0, usePublishedDatasourceLineageLabelsLazyQuery_1.usePublishedDatasourceLineageLabelsLazyQuery)({
        item,
        controls,
        includeUpstream,
        includeParented,
        contextOverrides,
        document: catalog_queries_1.PublishedDatasourceLineageLabelsDocument,
    });
    const embeddedDatasource = (0, useGeneralLineageLabelsLazyQuery_1.useGeneralLineageLabelsLazyQuery)({
        item,
        controls,
        includeUpstream,
        includeParented,
        contextOverrides,
        document: catalog_queries_1.EmbeddedDatasourceLineageLabelsDocument,
    });
    const virtualConnection = (0, useVirtualConnectionLineageLabelsLazyQuery_1.useVirtualConnectionLineageLabelsLazyQuery)({
        item,
        controls,
        includeUpstream,
        includeParented,
        contextOverrides,
        document: catalog_queries_1.VirtualConnectionLineageLabelsDocument,
    });
    const view = (0, useGeneralLineageLabelsLazyQuery_1.useGeneralLineageLabelsLazyQuery)({
        item,
        controls,
        includeUpstream,
        includeParented,
        contextOverrides,
        document: catalog_queries_1.ViewLineageLabelsDocument,
    });
    const workbook = (0, useWorkbookLineageLabelsLazyQuery_1.useWorkbookLineageLabelsLazyQuery)({
        item,
        controls,
        includeUpstream,
        includeParented,
        contextOverrides,
        document: catalog_queries_1.WorkbookLineageLabelsDocument,
    });
    const field = (0, useFieldLineageLabelsLazyQuery_1.useFieldLineageLabelsLazyQuery)({
        item,
        controls,
        includeUpstream,
        includeParented,
        contextOverrides,
        document: catalog_queries_1.FieldLineageLabelsDocument,
    });
    const metric = (0, useGeneralLineageLabelsLazyQuery_1.useGeneralLineageLabelsLazyQuery)({
        item,
        controls,
        includeParented,
        includeUpstream,
        contextOverrides,
        document: catalog_queries_1.MetricLineageLabelsDocument,
    });
    const flowField = (0, useFlowFieldLineageLabelsLazyQuery_1.useFlowFieldLineageLabelsLazyQuery)({
        item,
        controls,
        includeUpstream,
        includeParented,
        contextOverrides,
        document: catalog_queries_1.FlowFieldLineageLabelsDocument,
    });
    const flowColumnField = (0, useFlowColumnFieldLineageLabelsLazyQuery_1.useFlowColumnFieldLineageLabelsLazyQuery)({
        item,
        controls,
        includeUpstream,
        includeParented,
        contextOverrides,
        document: catalog_queries_1.FlowColumnFieldLineageLabelsDocument,
    });
    switch (item.subjectArea) {
        case 'column':
        case 'Column':
            return column;
        case 'database':
        case 'CloudFile':
        case 'DatabaseServer':
        case 'File':
        case 'WebDataConnector':
            return database;
        case 'table':
        case 'DatabaseTable':
            return databaseTable;
        case 'virtualConnectionTable':
        case 'VirtualConnectionTable':
            return virtualConnectionTable;
        case 'flow':
        case 'Flow':
            return flow;
        case 'datasource':
        case 'PublishedDatasource':
            return publishedDatasource;
        case 'EmbeddedDatasource':
            return embeddedDatasource;
        case 'virtualConnection':
        case 'VirtualConnection':
            return virtualConnection;
        case 'View':
            return view;
        case 'Workbook':
        case 'workbook':
            return workbook;
        case 'Field':
            return field;
        case 'FlowColumnOutputField':
            return flowColumnField;
        case 'FlowFieldOutputField':
            return flowField;
        case 'Metric':
        case 'metric':
            return metric;
        default: // Should never hit this
            (0, catalog_core_1.assertUnreachable)((_a = item.subjectArea) !== null && _a !== void 0 ? _a : 'undefined');
            return [() => {
                    return;
                }, {}];
    }
}
function mapRestLabelsToRenderProps(labels, authors) {
    const activeLabels = labels ? labels.filter((p) => p.active) : [];
    const customLabelResponse = activeLabels.filter((p) => !(0, LabelCategories_1.isBuiltInLabelCategory)(p.category));
    const customLabels = new Map();
    customLabelResponse.forEach(s => {
        var _a, _b, _c, _d, _e, _f, _g;
        !customLabels.has(s.category)
            ? customLabels.set(s.category, [{
                    type: s.value,
                    active: s.active,
                    author: (_c = authors === null || authors === void 0 ? void 0 : authors.get((_b = (_a = s.owner) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '')) !== null && _c !== void 0 ? _c : (s.userDisplayName ? { username: '', name: s.userDisplayName, domain: '' } : undefined),
                    message: s.message,
                    updatedAt: catalog_messages_1.Formatters.formatMediumDateTimeWithoutSeconds(new Date(s.updatedAt)),
                }])
            : (_d = customLabels.get(s.category)) === null || _d === void 0 ? void 0 : _d.push({
                type: s.value,
                active: s.active,
                author: (_g = authors === null || authors === void 0 ? void 0 : authors.get((_f = (_e = s.owner) === null || _e === void 0 ? void 0 : _e.id) !== null && _f !== void 0 ? _f : '')) !== null && _g !== void 0 ? _g : (s.userDisplayName ? { username: '', name: s.userDisplayName, domain: '' } : undefined),
                message: s.message,
                updatedAt: catalog_messages_1.Formatters.formatMediumDateTimeWithoutSeconds(new Date(s.updatedAt)),
            });
    });
    return ({
        warnings: activeLabels.filter((p) => p.category.toLowerCase() === 'warning').map(warning => {
            var _a, _b, _c;
            return {
                severe: warning.elevated,
                type: warning.value,
                active: warning.active,
                author: (_c = authors === null || authors === void 0 ? void 0 : authors.get((_b = (_a = warning.owner) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '')) !== null && _c !== void 0 ? _c : (warning.userDisplayName ? { username: '', name: warning.userDisplayName, domain: '' } : undefined),
                message: warning.message,
                updatedAt: catalog_messages_1.Formatters.formatMediumDateTimeWithoutSeconds(new Date(warning.updatedAt)),
            };
        }),
        certifications: activeLabels
            .filter((p) => p.category.toLowerCase() === 'certification')
            .map(c => {
            var _a, _b, _c;
            return {
                type: c.value,
                active: c.active,
                author: (_c = authors === null || authors === void 0 ? void 0 : authors.get((_b = (_a = c.owner) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '')) !== null && _c !== void 0 ? _c : (c.userDisplayName ? { username: '', name: c.userDisplayName, domain: '' } : undefined),
                message: c.message,
                updatedAt: catalog_messages_1.Formatters.formatMediumDateTimeWithoutSeconds(new Date(c.updatedAt)),
            };
        }),
        sensitivities: activeLabels
            .filter((p) => p.category.toLowerCase() === 'sensitivity')
            .map(s => {
            var _a, _b, _c;
            return {
                severe: s.elevated,
                type: s.value,
                active: s.active,
                author: (_c = authors === null || authors === void 0 ? void 0 : authors.get((_b = (_a = s.owner) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : '')) !== null && _c !== void 0 ? _c : (s.userDisplayName ? { username: '', name: s.userDisplayName, domain: '' } : undefined),
                message: s.message,
                updatedAt: catalog_messages_1.Formatters.formatMediumDateTimeWithoutSeconds(new Date(s.updatedAt)),
            };
        }),
        customLabels: customLabels,
    });
}
function useLabelsRest(item, catalogEnabled) {
    var _a;
    const queryFn = (0, catalog_core_1.useRestFn)(getRestLabels);
    const [enabled, setEnabled] = react_1.default.useState(false);
    const invoke = react_1.default.useCallback(() => setEnabled(true), []);
    const { data: _labels, isLoading: _labelsLoading } = (0, react_query_1.useQuery)(['labels', item], () => queryFn({ items: item }), { enabled });
    const users = (_a = _labels === null || _labels === void 0 ? void 0 : _labels.filter(l => l.owner !== undefined).map(l => { var _a; return (_a = l.owner) === null || _a === void 0 ? void 0 : _a.id; })) !== null && _a !== void 0 ? _a : [];
    const { authors, loading: _usersLoading } = (0, getUsers_1.useGetUsers)(users);
    const { warnings, certifications, sensitivities, customLabels } = mapRestLabelsToRenderProps(_labels, authors);
    const warningProps = warnings.length > 0 && catalogEnabled
        ? {
            category: 'warning',
            categoryType: 'warning',
            content: item,
            kind: 'hydrated',
            anchored: {
                category: 'warning',
                labels: warnings,
            },
        }
        : undefined;
    const certificationProps = certifications.length > 0
        ? {
            category: 'certification',
            categoryType: 'certification',
            content: item,
            kind: 'hydrated',
            anchored: {
                category: 'certification',
                labels: certifications,
            },
        }
        : undefined;
    const sensitivityProps = sensitivities.length > 0 && catalogEnabled
        ? {
            category: 'sensitivity',
            categoryType: 'sensitivity',
            content: item,
            kind: 'hydrated',
            anchored: {
                category: 'sensitivity',
                labels: sensitivities,
            },
        }
        : undefined;
    const customProps = new Map();
    customLabels.forEach((labels, category) => customProps.set(category, {
        category: category,
        categoryType: 'custom',
        content: item,
        kind: 'hydrated',
        anchored: {
            category: category,
            labels: labels,
        },
    }));
    return [invoke, {
            loading: _usersLoading || _labelsLoading,
            warnings: warningProps,
            certifications: certificationProps,
            sensitivities: sensitivityProps,
            customLabels: customProps,
        }];
}
function useInitialCallInvoke(invoke, probablyOrEager) {
    const [calledInitialInvoke, setCalledInitialInvoke] = react_1.default.useState(false);
    if (!calledInitialInvoke && probablyOrEager === 'eager') {
        invoke();
        setCalledInitialInvoke(true);
    }
}
function merge(rest, graph, content, customLabelCategoriesEnabled) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
    let certifications = undefined;
    let warnings = undefined;
    let sensitivities = undefined;
    const customLabels = new Map();
    if (((_a = graph.certifications) === null || _a === void 0 ? void 0 : _a.parented) || ((_b = rest.certifications) === null || _b === void 0 ? void 0 : _b.anchored)) {
        certifications = {
            category: 'certification',
            categoryType: 'certification',
            content: content,
            kind: 'hydrated',
            anchored: (_c = rest.certifications) === null || _c === void 0 ? void 0 : _c.anchored,
            parented: (_d = graph.certifications) === null || _d === void 0 ? void 0 : _d.parented,
        };
    }
    if (((_e = graph.warnings) === null || _e === void 0 ? void 0 : _e.parented) || ((_f = graph.warnings) === null || _f === void 0 ? void 0 : _f.upstream) || ((_g = rest.warnings) === null || _g === void 0 ? void 0 : _g.anchored)) {
        warnings = {
            category: 'warning',
            categoryType: 'warning',
            content: content,
            kind: 'hydrated',
            upstream: (_h = graph.warnings) === null || _h === void 0 ? void 0 : _h.upstream,
            anchored: (_j = rest.warnings) === null || _j === void 0 ? void 0 : _j.anchored,
            parented: (_k = graph.warnings) === null || _k === void 0 ? void 0 : _k.parented,
        };
    }
    if ((((_l = graph.sensitivities) === null || _l === void 0 ? void 0 : _l.upstream) || ((_m = graph.sensitivities) === null || _m === void 0 ? void 0 : _m.parented) || ((_o = rest.sensitivities) === null || _o === void 0 ? void 0 : _o.anchored))) {
        sensitivities = {
            category: 'sensitivity',
            categoryType: 'sensitivity',
            content: content,
            kind: 'hydrated',
            upstream: (_p = graph.sensitivities) === null || _p === void 0 ? void 0 : _p.upstream,
            anchored: (_q = rest.sensitivities) === null || _q === void 0 ? void 0 : _q.anchored,
            parented: (_r = graph.sensitivities) === null || _r === void 0 ? void 0 : _r.parented,
        };
    }
    // merge graph map and rest map
    if (customLabelCategoriesEnabled && (graph.customLabels || rest.customLabels)) {
        const customCategories = new Set([...((_s = graph
                .customLabels) !== null && _s !== void 0 ? _s : new Map())
                .keys()]
            .concat([...((_t = rest.customLabels) !== null && _t !== void 0 ? _t : new Map()).keys()]));
        customCategories.forEach((category) => {
            var _a, _b;
            const graphCustomLabels = (_a = graph.customLabels) === null || _a === void 0 ? void 0 : _a.get(category);
            const restCustomLabels = (_b = rest.customLabels) === null || _b === void 0 ? void 0 : _b.get(category);
            customLabels.set(category, {
                category: category,
                categoryType: 'custom',
                content: content,
                kind: 'hydrated',
                upstream: graphCustomLabels === null || graphCustomLabels === void 0 ? void 0 : graphCustomLabels.upstream,
                anchored: restCustomLabels === null || restCustomLabels === void 0 ? void 0 : restCustomLabels.anchored,
                parented: graphCustomLabels === null || graphCustomLabels === void 0 ? void 0 : graphCustomLabels.parented,
            });
        });
    }
    return {
        loading: (_u = (rest.loading || graph.loading)) !== null && _u !== void 0 ? _u : undefined,
        error: (_w = (_v = rest.error) !== null && _v !== void 0 ? _v : graph.error) !== null && _w !== void 0 ? _w : undefined,
        certifications: certifications,
        warnings: warnings,
        sensitivities: sensitivities,
        customLabels: customLabels,
    };
}
function useLabels({ catalogEnabled, content, includeUpstream, includeParented, controls, contextOverrides, probablyOrEager }) {
    const { customLabelCategoriesEnabled } = (0, catalog_core_1.useContextWithOverride)(catalog_core_1.FeatureFlagsContext, contextOverrides === null || contextOverrides === void 0 ? void 0 : contextOverrides.featureFlags);
    const [invokeRest, dataRest] = useLabelsRest(content, catalogEnabled);
    const [invokeGraph, dataGraph] = useLabelsGraph(content, controls, includeUpstream, includeParented, contextOverrides);
    const invoke = react_1.default.useCallback(() => {
        invokeRest();
        if (catalogEnabled && (includeUpstream || includeParented)) {
            invokeGraph();
        }
    }, [catalogEnabled, includeUpstream, includeParented, invokeRest, invokeGraph]);
    useInitialCallInvoke(invoke, probablyOrEager);
    return [invoke, merge(dataRest, dataGraph, content, customLabelCategoriesEnabled)];
}
function isSevere(probably, data) {
    var _a, _b, _c, _d;
    if (probably === null || probably === void 0 ? void 0 : probably.warning) {
        return (_a = probably === null || probably === void 0 ? void 0 : probably.warning) === null || _a === void 0 ? void 0 : _a.severe;
    }
    let severe = false;
    if (data) {
        (_b = data.anchored) === null || _b === void 0 ? void 0 : _b.labels.forEach(label => {
            if (label.severe) {
                severe = true;
            }
        });
        (_c = data.upstream) === null || _c === void 0 ? void 0 : _c.labels.forEach(label => {
            if (label.severe) {
                severe = true;
            }
        });
        (_d = data.parented) === null || _d === void 0 ? void 0 : _d.labels.forEach(label => {
            if (label.severe) {
                severe = true;
            }
        });
    }
    return severe;
}
function labelValue(defaultType, probably, data) {
    var _a;
    if (probably === null || probably === void 0 ? void 0 : probably.warning) {
        return probably.warning.value;
    }
    let type = defaultType;
    (_a = data === null || data === void 0 ? void 0 : data.anchored) === null || _a === void 0 ? void 0 : _a.labels.forEach(label => {
        type = label.type.toString();
    });
    return type;
}
function buildWarnings({ warnings, loading }, _a, invoke, emitTelemetry) {
    var { catalogEnabled, content, probablyOrEager, includeUpstream: _includeUpstream, includeParented: includeParented } = _a, shared = tslib_1.__rest(_a, ["catalogEnabled", "content", "probablyOrEager", "includeUpstream", "includeParented"]);
    if (!catalogEnabled) {
        return null;
    }
    const onClick = () => {
        invoke();
        emitTelemetry({ elevated: isSevere(probably, warnings), value: labelValue('WARNING', probably, warnings) });
    };
    const probably = probablyOrEager;
    if ((!warnings || loading) && (probably === null || probably === void 0 ? void 0 : probably.warning)) {
        return (react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({ category: 'warning', categoryType: 'warning', kind: 'dehydrated', severe: isSevere(probably, warnings), content: content }, shared, { loading: loading !== null && loading !== void 0 ? loading : false, onClick: onClick, count: probably.warning.count, labelValue: probably.warning.value })));
    }
    if (warnings) {
        return react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({}, warnings, shared, { loading: loading !== null && loading !== void 0 ? loading : false, onClick: onClick }));
    }
    return null;
}
function buildCertifications({ certifications, loading }, _a, invoke, emitTelemetry) {
    var { catalogEnabled, content, probablyOrEager, includeUpstream: _includeUpstream, includeParented: includeParented } = _a, shared = tslib_1.__rest(_a, ["catalogEnabled", "content", "probablyOrEager", "includeUpstream", "includeParented"]);
    const onClick = () => {
        invoke();
        emitTelemetry({ elevated: false, value: labelValue('CERTIFIED', probably, certifications) });
    };
    const probably = probablyOrEager;
    if ((!certifications || loading) && (probably === null || probably === void 0 ? void 0 : probably.certified)) {
        return (react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({ category: 'certification', categoryType: 'certification', kind: 'dehydrated', content: content }, shared, { onClick: onClick, loading: loading !== null && loading !== void 0 ? loading : false })));
    }
    if (certifications) {
        return react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({}, certifications, shared, { onClick: onClick, loading: loading !== null && loading !== void 0 ? loading : false }));
    }
    return null;
}
function buildSensitivities({ sensitivities, loading }, _a, invoke, emitTelemetry) {
    var { catalogEnabled, content, probablyOrEager, includeUpstream: _includeUpstream, includeParented: includeParented } = _a, shared = tslib_1.__rest(_a, ["catalogEnabled", "content", "probablyOrEager", "includeUpstream", "includeParented"]);
    if (!catalogEnabled) {
        return null;
    }
    const onClick = () => {
        invoke();
        emitTelemetry({ elevated: false, value: labelValue('SENSITIVE_DATA', probably, sensitivities) });
    };
    const probably = probablyOrEager;
    if ((!sensitivities || loading) && (probably === null || probably === void 0 ? void 0 : probably.sensitivity)) {
        return ( // TODO we should not defaulting to severity is true here
        react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({ severe: true, category: 'sensitivity', categoryType: 'sensitivity', kind: 'dehydrated', content: content }, shared, { onClick: onClick, loading: loading !== null && loading !== void 0 ? loading : false, count: probably.sensitivity.count, labelValue: probably.sensitivity.value })));
    }
    if (sensitivities) {
        return react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({}, sensitivities, shared, { onClick: onClick, loading: loading !== null && loading !== void 0 ? loading : false }));
    }
    return null;
}
function buildCustom({ customLabels, loading }, _a, invoke, emitTelemetry) {
    var _b, _c, _d;
    var { catalogEnabled, content, probablyOrEager, includeUpstream: _includeUpstream, includeParented: includeParented } = _a, shared = tslib_1.__rest(_a, ["catalogEnabled", "content", "probablyOrEager", "includeUpstream", "includeParented"]);
    if (!catalogEnabled) {
        return null;
    }
    const onClick = () => {
        var _a;
        invoke();
        emitTelemetry({
            elevated: false,
            value: labelValue('CUSTOM', probably, Array.from((_a = customLabels === null || customLabels === void 0 ? void 0 : customLabels.values()) !== null && _a !== void 0 ? _a : [])[0]), // TODO CAROLINE FIX
        });
    };
    const probably = probablyOrEager;
    if ((!customLabels || loading) && (probably === null || probably === void 0 ? void 0 : probably.custom)) {
        const numChips = probably.custom.length;
        const numChipstoDisplay = (_b = shared.chipDisplayCount) !== null && _b !== void 0 ? _b : defaultChipDisplayCount;
        const dehydratedCustomLabels = [];
        probably.custom.forEach((e) => {
            dehydratedCustomLabels.push(react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({ category: e.category, categoryType: 'custom', kind: 'dehydrated', content: content }, shared, { onClick: onClick, loading: loading !== null && loading !== void 0 ? loading : false, count: e.count, labelValue: e.value })));
        });
        return createFlexCustom({
            labels: dehydratedCustomLabels,
            numChips: numChips,
            numChipsToDisplay: numChipstoDisplay,
        });
    }
    if (customLabels) {
        const customLabelPropsList = [...((_c = customLabels === null || customLabels === void 0 ? void 0 : customLabels.values()) !== null && _c !== void 0 ? _c : [])];
        const customLabelsList = [];
        customLabelPropsList.forEach((e) => {
            const element = react_1.default.createElement(LabelButtons_1.LabelButton, Object.assign({}, e, shared, { onClick: onClick, loading: loading !== null && loading !== void 0 ? loading : false }));
            customLabelsList.push(element);
        });
        const numChips = customLabelsList.length;
        const numChipsToDisplay = (_d = shared.chipDisplayCount) !== null && _d !== void 0 ? _d : numChips;
        return createFlexCustom({ labels: customLabelsList, numChips: numChips, numChipsToDisplay });
    }
    return null;
}
function createFlexCustom(props) {
    const { labels, numChips: numChips, numChipsToDisplay } = props;
    const displayedCustomLabels = (numChips > 0)
        ? (react_1.default.createElement(react_1.default.Fragment, null, labels.map((i, index) => {
            if (index < numChipsToDisplay) {
                return (react_1.default.createElement(react_1.default.Fragment, { key: index }, wrapper(i)));
            }
            else {
                return null;
            }
        })))
        : undefined;
    const hiddenCustomLabels = (numChipsToDisplay <= numChips)
        ? (react_1.default.createElement(react_1.default.Fragment, null, labels.map((i, index) => {
            if (index >= numChipsToDisplay) {
                return (react_1.default.createElement(react_1.default.Fragment, { key: index }, wrapper(i)));
            }
            else {
                return null;
            }
        })))
        : undefined;
    return {
        displayedCustomLabels: displayedCustomLabels,
        hiddenCustomLabels: hiddenCustomLabels,
        numCustomLabels: numChips,
    };
}
function useBuildTelemetryEvents(content_Type, testId, source, telemetryContext) {
    const telemetry = (0, catalog_core_1.useContextWithOverride)(TelemetryContext_1.ReactTelemetryContext, telemetryContext);
    const emitWarning = (0, useTelemetrySender_1.useTelemetrySender)({
        type: 'label_opened',
        category: 'warning',
        source: source !== null && source !== void 0 ? source : testId,
        content_type: content_Type !== null && content_Type !== void 0 ? content_Type : undefined,
    }, 'empty', telemetry);
    const emitCertification = (0, useTelemetrySender_1.useTelemetrySender)({
        type: 'label_opened',
        category: 'certification',
        source: source !== null && source !== void 0 ? source : testId,
        content_type: content_Type !== null && content_Type !== void 0 ? content_Type : undefined,
    }, 'empty', telemetry);
    const emitSensitivity = (0, useTelemetrySender_1.useTelemetrySender)({
        type: 'label_opened',
        category: 'sensitivity',
        source: source !== null && source !== void 0 ? source : testId,
        content_type: content_Type !== null && content_Type !== void 0 ? content_Type : undefined,
    }, 'empty', telemetry);
    const emitCustomLabel = (0, useTelemetrySender_1.useTelemetrySender)({
        type: 'label_opened',
        category: 'custom',
        source: source !== null && source !== void 0 ? source : testId,
        content_type: content_Type !== null && content_Type !== void 0 ? content_Type : undefined,
    }, 'empty', telemetry);
    return [emitWarning, emitCertification, emitSensitivity, emitCustomLabel];
}
function useRenderLabels(params) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
    const [invoke, data] = useLabels(params);
    const [emitWarningTelemetryEvent, emitCertificationTelemetryEvent, emitSensitivityTelemetryEvent, emitCustomTelemetryEvent,] = useBuildTelemetryEvents(params.content.contentType, params.testId, params.source, (_a = params.contextOverrides) === null || _a === void 0 ? void 0 : _a.telemetryContext);
    const debouncedData = (0, useLockInLoadedValue_1.useLockInLoadedValue)(data);
    const [showMore, setShowMore] = (0, react_1.useState)(false);
    const warnings = buildWarnings(debouncedData, params, invoke, emitWarningTelemetryEvent);
    const certifications = buildCertifications(debouncedData, params, invoke, emitCertificationTelemetryEvent);
    const sensitivities = buildSensitivities(debouncedData, params, invoke, emitSensitivityTelemetryEvent);
    const customLabelElements = buildCustom(debouncedData, params, invoke, emitCustomTelemetryEvent);
    const displayedCustomLabels = (_b = customLabelElements === null || customLabelElements === void 0 ? void 0 : customLabelElements.displayedCustomLabels) !== null && _b !== void 0 ? _b : null;
    const hiddenCustomLabels = (_c = customLabelElements === null || customLabelElements === void 0 ? void 0 : customLabelElements.hiddenCustomLabels) !== null && _c !== void 0 ? _c : null;
    const numCustomLabels = (_d = customLabelElements === null || customLabelElements === void 0 ? void 0 : customLabelElements.numCustomLabels) !== null && _d !== void 0 ? _d : 0;
    const warningElement = warnings ? wrapper(warnings) : null;
    const certificationElement = certifications ? wrapper(certifications) : null;
    const sensitivityElement = sensitivities ? wrapper(sensitivities) : null;
    const customLabelsElement = (react_1.default.createElement(react_1.default.Fragment, null, customLabelElements === null || customLabelElements === void 0 ? void 0 :
        customLabelElements.displayedCustomLabels, customLabelElements === null || customLabelElements === void 0 ? void 0 :
        customLabelElements.hiddenCustomLabels));
    const showMoreButton = (numCustomLabels > ((_e = params.chipDisplayCount) !== null && _e !== void 0 ? _e : defaultChipDisplayCount))
        ? (wrapper(react_1.default.createElement(widgets_1.ButtonWidget, { onClick: () => {
                setShowMore(!showMore);
            }, id: 'showMoreTextButton', buttonType: widgets_1.ButtonType.Icon }, showMore ? catalog_messages_1.Messages.LabelCustomShowLess() : catalog_messages_1.Messages.LabelCustomShowMoreWithCount({
            MESSAGE_COUNT: (numCustomLabels - ((_f = params.chipDisplayCount) !== null && _f !== void 0 ? _f : 0)).toString(),
        }))))
        : null;
    const showMoreButtonLink = (numCustomLabels > ((_g = params.chipDisplayCount) !== null && _g !== void 0 ? _g : defaultChipDisplayCount))
        ? (wrapper(react_1.default.createElement("a", { href: params.content.contentUrl, rel: 'noreferrer' },
            react_1.default.createElement(widgets_1.ButtonWidget, { id: 'showMoreTextButton', buttonType: widgets_1.ButtonType.Icon }, showMore ? catalog_messages_1.Messages.LabelCustomShowLess() : catalog_messages_1.Messages.LabelCustomShowMoreWithCount({
                MESSAGE_COUNT: (numCustomLabels - ((_h = params.chipDisplayCount) !== null && _h !== void 0 ? _h : 0)).toString(),
            })))))
        : null;
    if (params.displayCategories) { // assumes either icon or grid experience
        const { warning, certification, sensitivity, custom } = params.displayCategories;
        if (((_j = params.controls) === null || _j === void 0 ? void 0 : _j.kind) === 'icon') {
            return (react_1.default.createElement(react_1.default.Fragment, null,
                warning ? warningElement : null,
                certification ? certificationElement : null,
                sensitivity ? sensitivityElement : null,
                custom ? customLabelsElement : null));
        }
        return (react_1.default.createElement("div", { style: { display: 'inline-flex', gap: '8px' } },
            warning ? warningElement : null,
            certification ? certificationElement : null,
            sensitivity ? sensitivityElement : null,
            displayedCustomLabels,
            showMore ? hiddenCustomLabels : null,
            ((_k = params.content.contentType) === null || _k === void 0 ? void 0 : _k.toLowerCase()) === 'column' ? showMoreButtonLink : showMoreButton));
    }
    else {
        return (react_1.default.createElement(StickersFlexBox_1.FlexBoxDiv, null,
            warnings,
            certifications,
            sensitivities,
            displayedCustomLabels,
            showMore ? hiddenCustomLabels : null,
            showMoreButton));
    }
}
exports.useRenderLabels = useRenderLabels;
const wrapper = (e) => react_1.default.createElement(Styles_1.StickerChipStyle, null, e);
//# sourceMappingURL=useRenderLabels.js.map